import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { 
  SprayBottleIcon, 
  WrenchIcon, 
  MagnifyingGlassIcon, 
  HeartIcon, 
  ListIcon,
  CaretDoubleDownIcon,
  CaretDownIcon,
  CaretRightIcon,
  CaretUpIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';

interface Department {
  name: string;
  group: string;
}

interface TaskBadgeProps {
  task: any;
  currentDate: string;
  propertyId: string;
  index: number;
  departments: Department[];
  bubbleInstance?: any;
}

// Department group to Phosphor icon mapping
const departmentGroupIcons = {
  'Housekeeping': SprayBottleIcon,
  'Maintenance': WrenchIcon,
  'Inspection': MagnifyingGlassIcon,
  'Guest Experience': HeartIcon,
  'Other': ListIcon
} as const;

// Helper function to get icon component for a department group
const getDepartmentIcon = (group: string): React.ComponentType<any> => {
  return departmentGroupIcons[group] || departmentGroupIcons['Other'];
};

// Centralized priority icon logic
const getPriorityIcon = (priority: string) => {
  const normalizedPriority = priority.toLowerCase();
  switch (normalizedPriority) {
    case 'urgent':
      return { Icon: WarningCircleIcon, color: 'var(--color-destructive)', weight: 'regular' };
    case 'high':
      return { Icon: CaretUpIcon, color: 'var(--color-warning)', weight: 'regular' };
    case 'medium':
      return { Icon: CaretRightIcon, color: 'var(--color-success)', weight: 'regular' };
    case 'low':
      return { Icon: CaretDownIcon, color: 'var(--color-blue)', weight: 'regular' };
    case 'watch':
      return { Icon: CaretDoubleDownIcon, color: 'var(--color-light-purple)', weight: 'regular' };
    default:
      return { Icon: ListIcon, color: 'var(--color-stroke-strong)', weight: 'regular' };
  }
};

// Task status color mapping
const getTaskStatusColor = (status: string) => {
  const normalizedStatus = status.toLowerCase();
  switch (normalizedStatus) {
    case 'open':
    case 'not_started':
    case 'pending':
      return 'var(--color-stroke-strong)';
    case 'in_progress':
    case 'in_review':
    case 'active':
      return 'var(--color-blue)';
    case 'done':
    case 'completed':
    case 'finished':
      return 'var(--color-success)';
    case 'paused':
    case 'on_hold':
      return 'var(--color-warning)';
    case 'cancelled':
    case 'archived':
      return 'var(--color-text-weak)';
    default:
      return 'var(--color-blue)';
  }
};

// Helper function to check if task is overdue
const isTaskOverdue = (task: any) => {
  const dueDate = task.dueDate || task.due_date;
  if (!dueDate) return false;
  
  const today = new Date().toISOString().slice(0, 10);
  const taskDueDate = new Date(dueDate).toISOString().slice(0, 10);
  const status = (task.status || task.task_status || '').toLowerCase();
  
  // Task is overdue if due date is in the past and status is not "done" or "completed"
  return taskDueDate < today && !['done', 'completed', 'finished'].includes(status);
};

// Helper function to render task content
const renderTaskContent = (task: any, departments: Department[]) => {
  const taskDepartment = departments.find((dept: any) => dept.name === task.task_department);
  const departmentGroup = taskDepartment?.group || 'Other';
  const IconComponent = getDepartmentIcon(departmentGroup);
  
  const priorityInfo = getPriorityIcon(task.task_priority || 'default');
  const PriorityIcon = priorityInfo.Icon;
  
  const statusColor = getTaskStatusColor(task.status || task.task_status || 'todo');
  const isOverdue = isTaskOverdue(task);

  return (
    <div className="task-content" style={{
      borderLeft: `3px solid ${statusColor}`,
      borderRadius: '8px',
      marginTop: '-8px',
      padding: '8px',     
      backgroundColor: isOverdue ? 'var(--color-light-red)' : undefined
    }}>
      <div className='task-header'>
        <div className='task-tag'> 
          <IconComponent className="department-icon" size={12} style={{ marginRight: '4px' }} />
          <span className="overflow-text">{task.task_department}</span>
        </div>
        <div className='task-tag priority' style={{
          borderColor: priorityInfo.color,
          position: 'relative',
          backgroundColor: priorityInfo.color
        }}>
          <span>{task.task_priority}</span>
        </div>
      </div>
      <span className="task-title">{task.name || task.title || 'Task'}</span>
    </div>
  );
};

// Draggable Task Component
export const DraggableTask: React.FC<TaskBadgeProps> = ({ 
  task, 
  currentDate, 
  propertyId, 
  index, 
  departments, 
  bubbleInstance 
}) => {
  const taskId = `task-${task.task_id || task.id}-${currentDate}-${propertyId}`;
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: taskId,
    data: {
      task,
      currentDate,
      propertyId,
    },
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
  } : undefined;

  const priorityClass = `task-priority-${task.priority || 'default'}`;

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`task-badge ${priorityClass} ${isDragging ? 'task-dragging' : ''}`}
      {...listeners}
      {...attributes}
      onClick={() => {
        //console.log('🎯 Task clicked:', task.task_id);
        if (bubbleInstance?.publishState && bubbleInstance?.triggerEvent) {
          bubbleInstance.publishState('selected_task_id', task.task_id || '');
          bubbleInstance.triggerEvent('task_clicked');
        }
      }}
    >
      {renderTaskContent(task, departments)}
    </div>
  );
};

// Task Drag Overlay Component
export const TaskDragOverlay: React.FC<{ task: any; departments: Department[] }> = ({ 
  task, 
  departments 
}) => {
  return (
    <div className="task-badge task-drag-overlay">
      {renderTaskContent(task, departments)}
    </div>
  );
};

// Export helper functions for reuse
export { getPriorityIcon, getDepartmentIcon, renderTaskContent, getTaskStatusColor };